import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { isEmpty, isFunction, isNull } from 'lodash';
import PropTypes from 'prop-types';
import qs from 'querystring';
import * as Yup from 'yup';

import { Modal } from '~/components';
import { Input, Select, Autocomplete } from '~/components/Form';
import { t, validCharacters } from '~/helpers';
import { getToastErrors } from '~/helpers/errorsRequest';
import { validation, validationErrors } from '~/helpers/yupValidations';
import api from '~/services/api';
import { submitting } from '~/store/modules/operacaovazaonaoautorizada/actions';

import Confirmation from '../Confirmation';
import * as S from './styles';

export default function Casting({
  close,
  sending,
  visible,
  data,
  onCallback,
  toggleDisabled,
  toggleLoading,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [justification, setJustification] = useState(false);
  const [isChildren, setIsChildren] = useState(false);

  const { formaidfrentistaabast } = useSelector((state) => state.user.configs);

  const formRef = useRef(null);
  const modalRef = useRef(null);
  const dispatch = useDispatch();
  const redux = useSelector(
    (state) => state.operacaovazaonaoautorizada.submitting
  );

  const options = useMemo(
    () => [
      {
        label: t('s.menu.relatorio.abastecimento'),
        value: 'A',
      },
      {
        label: t('s.menu.relatorio.lubrificacao'),
        value: 'L',
      },
      {
        label: t('s.operacao.transferencia'),
        value: 'T',
      },
    ],
    []
  );

  const handleSubmit = useCallback(
    async (formData) => {
      try {
        toggleDisabled(true);
        toggleLoading(true);
        setIsChildren(true);

        const schema = Yup.object().shape({
          tipomovimento: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.campoobrigatorio')),
          idjustificativacontingencia: justification
            ? Yup.string()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string().nullable(),
          codigousuario:
            formaidfrentistaabast !== 0
              ? Yup.string()
                  .typeError(t('s.geral.validacao.campoobrigatorio'))
                  .test(
                    'characters-invalid',
                    t('s.configuracao.geral.naopodecontercaracterespecial'),
                    (value) => validCharacters(value)
                  )
                  .required(t('s.geral.validacao.campoobrigatorio'))
              : Yup.string().nullable(),
        });

        await validation(schema, formData);

        const formDataSubmit = {
          codigousuario: '',
          ...data,
          ...formData,
          idjustificativacontingencia: justification
            ? formData.idjustificativacontingencia
            : null,
        };

        delete formDataSubmit.idfrotacomboio;
        delete formDataSubmit.nomeempresaveiculo;
        delete formDataSubmit.totalizadorencerrante;

        delete formDataSubmit.motivomovimentacao;

        await api.put(
          '/movimentacoesvazoesnaoautorizadas/transformamovimentacao',
          formDataSubmit
        );

        toast.success(t('s.geral.sucessoalteracao'));
        close();
      } catch (err) {
        setIsChildren(false);
        validationErrors(err, formRef);
      } finally {
        toggleDisabled(false);
        toggleLoading(false);
      }
    },
    [data, justification, formaidfrentistaabast, validCharacters]
  );

  const handleResponseAPIJustification = useCallback(async () => {
    try {
      toggleDisabled(true);

      const response = await api.get('/justificativascontingencias', {
        params: {
          pagesize: 99999,
          pesquisalista: true,
          wherefield: ['statusjustificativacontingencia'],
          valuewherefield: ['L'],
          typewherefield: ['EQUALS'],
        },
        paramsSerializer: (value) => qs.stringify(value),
      });

      setJustification(!isEmpty(response.data.items));
    } catch (err) {
      getToastErrors(err);
    } finally {
      toggleDisabled(false);
    }
  }, []);

  useEffect(() => {
    if (
      !visible &&
      !isSubmitting &&
      !isChildren &&
      isFunction(redux.onCallback)
    ) {
      redux.onCallback();
    }
  }, [visible, isSubmitting, redux.onCallback, isChildren]);

  useEffect(() => {
    if (!isEmpty(data) && isFunction(onCallback)) {
      dispatch(submitting({ data, onCallback }));
    }
  }, [data, onCallback]);

  useEffect(() => {
    if (sending) setIsSubmitting(true);
  }, [sending]);

  useEffect(() => {
    if (isSubmitting) {
      modalRef.current.handleOpenModal();
    }
  }, [isSubmitting]);

  useEffect(() => {
    handleResponseAPIJustification();
  }, []);

  if (!isEmpty(data))
    return (
      <>
        <S.WrapperForm ref={formRef} initialData={data} onSubmit={handleSubmit}>
          <Select
            name="tipomovimento"
            label={t('s.cadastro.veiculo.sap.movimentoveiculosap')}
            options={options}
            disabled
          />
          {data.tipomovimento !== 'T' && (
            <Input
              disabled
              name="nomeempresaveiculo"
              label={t('s.menu.relatorio.empresa')}
            />
          )}
          <Input
            disabled
            name="idfrotacomboio"
            label={t('s.menu.cadastro.veiculo')}
          />
          <Input
            disabled
            name="totalizadorencerrante"
            label={t('Km/Hora')}
            type="number"
          />
          {(isNull(data.codigousuario) || isEmpty(data.codigousuario)) &&
          formaidfrentistaabast !== 0 ? (
            <Input
              name="codigousuario"
              label={t('s.operacao.abastecimento.codigousuario')}
            />
          ) : (
            <Input
              disabled
              name="codigousuario"
              label={t('s.operacao.abastecimento.codigousuario')}
            />
          )}
          {justification && (
            <S.WrapperAutocomplete>
              <Autocomplete
                name="idjustificativacontingencia"
                url="justificativascontingencias"
                id="idjustificativacontingencia"
                label="descricaojustificativacontingencia"
                unique="idjustificativacontingencia"
                title={t('s.menu.operacao.justificativacontingencia')}
                params={{
                  pagesize: 99999,
                  pesquisalista: true,
                  wherefield: ['statusjustificativacontingencia'],
                  valuewherefield: ['L'],
                  typewherefield: ['EQUALS'],
                }}
              />
            </S.WrapperAutocomplete>
          )}
        </S.WrapperForm>

        <Modal
          ref={modalRef}
          form
          disableBackdropClick
          disableEscapeKeyDown
          maxWidth="xs"
          title={t('s.geral.botaoconfirmar')}
          onForm={() => {
            setIsChildren(true);
            formRef.current.submitForm();
          }}
          onClose={() => setIsSubmitting(false)}
          component={Confirmation}
        />
      </>
    );

  return <div />;
}

Casting.propTypes = {
  close: PropTypes.func.isRequired,
  sending: PropTypes.bool.isRequired,
  visible: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  onCallback: PropTypes.func.isRequired,
  toggleDisabled: PropTypes.func.isRequired,
  toggleLoading: PropTypes.func.isRequired,
};
